import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowRight,
  ArrowLeft,
  Download,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  TrendingUp,
  Target,
  Wallet,
  Lightbulb,
} from "lucide-react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import confetti from "canvas-confetti";

const PILLARS = [
  {
    name: "foundations",
    label: "Foundations",
    icon: ShieldCheck,
    questions: [
      {
        id: 1,
        pillar: "foundations",
        q: "How long has your business been trading for?",
        opts: [
          { a: "Less than 1 year", s: 1 },
          { a: "1-3 years", s: 2 },
          { a: "3-5 years", s: 3 },
          { a: "5+ years", s: 4 },
        ],
      },
      {
        id: 2,
        pillar: "foundations",
        q: "Do you have a separate business bank account?",
        opts: [
          { a: "Yes, fully separate", s: 4 },
          { a: "No", s: 0 },
        ],
      },
      {
        id: 3,
        pillar: "foundations",
        q: "Are you VAT registered?",
        opts: [
          { a: "Yes", s: 4 },
          { a: "No, but approaching the threshold", s: 2 },
          { a: "No, and not near the threshold", s: 1 },
        ],
      },
      {
        id: 4,
        pillar: "foundations",
        q: "Do you use a CRM system?",
        opts: [
          { a: "Yes, specialized window cleaning software", s: 4 },
          { a: "Yes, a general CRM", s: 3 },
          { a: "No, I use spreadsheets", s: 1 },
          { a: "No, I use a paper diary", s: 0 },
        ],
      },
      {
        id: 5,
        pillar: "foundations",
        q: "Do you have a professional website?",
        opts: [
          { a: "Yes, high-quality and optimized", s: 4 },
          { a: "Yes, but it's basic", s: 2 },
          { a: "No, just social media", s: 1 },
          { a: "No website at all", s: 0 },
        ],
      },
      {
        id: 6,
        pillar: "foundations",
        q: "Do you have public liability insurance?",
        opts: [
          { a: "Yes, fully covered", s: 4 },
          { a: "No", s: 0 },
        ],
      },
    ],
  },
  {
    name: "growth",
    label: "Growth",
    icon: TrendingUp,
    questions: [
      {
        id: 7,
        pillar: "growth",
        q: "How do you currently find new customers?",
        opts: [
          { a: "Google & Facebook Ads", s: 4 },
          { a: "Canvassing & Leaflets", s: 3 },
          { a: "Word of mouth only", s: 1 },
          { a: "I'm not actively looking", s: 0 },
        ],
      },
      {
        id: 8,
        pillar: "growth",
        q: "Do you track your Customer Acquisition Cost (CAC)?",
        opts: [
          { a: "Yes, exactly", s: 4 },
          { a: "Roughly", s: 2 },
          { a: "No", s: 0 },
        ],
      },
      {
        id: 9,
        pillar: "growth",
        q: "Do you have an automated booking system?",
        opts: [
          { a: "Yes, online booking & payment", s: 4 },
          { a: "No, I book everyone manually", s: 0 },
        ],
      },
      {
        id: 10,
        pillar: "growth",
        q: "How many new leads do you generate per month?",
        opts: [
          { a: "50+", s: 4 },
          { a: "20-50", s: 3 },
          { a: "5-20", s: 1 },
          { a: "Less than 5", s: 0 },
        ],
      },
      {
        id: 11,
        pillar: "growth",
        q: "Do you have a referral program in place?",
        opts: [
          { a: "Yes, automated", s: 4 },
          { a: "Yes, manual", s: 2 },
          { a: "No", s: 0 },
        ],
      },
      {
        id: 12,
        pillar: "growth",
        q: "Are you currently expanding your service area?",
        opts: [
          { a: "Yes, actively", s: 4 },
          { a: "Planning to", s: 2 },
          { a: "No", s: 0 },
        ],
      },
    ],
  },
  {
    name: "maturity",
    label: "Maturity",
    icon: Target,
    questions: [
      {
        id: 13,
        pillar: "maturity",
        q: "How many vans/teams do you have?",
        opts: [
          { a: "4+", s: 4 },
          { a: "2-3", s: 3 },
          { a: "1 van with an employee", s: 2 },
          { a: "Just me", s: 1 },
        ],
      },
      {
        id: 14,
        pillar: "maturity",
        q: "Do you have documented systems for your daily operations?",
        opts: [
          { a: "Yes, for everything", s: 4 },
          { a: "For some things", s: 2 },
          { a: "No, it's all in my head", s: 0 },
        ],
      },
      {
        id: 15,
        pillar: "maturity",
        q: "Could your business run for a month without you?",
        opts: [
          { a: "Yes, completely", s: 4 },
          { a: "Yes, but with issues", s: 2 },
          { a: "No, I am the business", s: 0 },
        ],
      },
      {
        id: 16,
        pillar: "maturity",
        q: "Do you have an exit strategy or 5-year plan?",
        opts: [
          { a: "Yes, clear plan", s: 4 },
          { a: "Vague idea", s: 2 },
          { a: "No", s: 0 },
        ],
      },
      {
        id: 17,
        pillar: "maturity",
        q: "What is your staff turnover rate?",
        opts: [
          { a: "Very low", s: 4 },
          { a: "Average", s: 2 },
          { a: "High", s: 0 },
          { a: "No staff yet", s: 2 },
        ],
      },
      {
        id: 18,
        pillar: "maturity",
        q: "Do you offer consistent training for your team?",
        opts: [
          { a: "Yes, regular sessions", s: 4 },
          { a: "Occasionally", s: 2 },
          { a: "No", s: 0 },
          { a: "No team yet", s: 2 },
        ],
      },
    ],
  },
  {
    name: "investment",
    label: "Investment",
    icon: Wallet,
    questions: [
      {
        id: 19,
        pillar: "investment",
        q: "What is your monthly marketing budget?",
        opts: [
          { a: "£1,000+", s: 4 },
          { a: "£500-£1,000", s: 3 },
          { a: "£100-£500", s: 1 },
          { a: "£0", s: 0 },
        ],
      },
      {
        id: 20,
        pillar: "investment",
        q: "Do you invest in modern equipment?",
        opts: [
          { a: "Yes, I have the best gear", s: 4 },
          { a: "Yes, standard gear", s: 3 },
          { a: "No, traditional only", s: 1 },
        ],
      },
      {
        id: 21,
        pillar: "investment",
        q: "Do you spend money on personal or business development?",
        opts: [
          { a: "Yes, regularly", s: 4 },
          { a: "Occasionally", s: 2 },
          { a: "No", s: 0 },
        ],
      },
      {
        id: 22,
        pillar: "investment",
        q: "What percentage of revenue do you reinvest?",
        opts: [
          { a: "10%+", s: 4 },
          { a: "5-10%", s: 3 },
          { a: "1-5%", s: 1 },
          { a: "0%", s: 0 },
        ],
      },
      {
        id: 23,
        pillar: "investment",
        q: "Do you have a budget for vehicle maintenance?",
        opts: [
          { a: "Yes, dedicated fund", s: 4 },
          { a: "I pay as I go", s: 2 },
          { a: "No plan", s: 0 },
        ],
      },
      {
        id: 24,
        pillar: "investment",
        q: "Do you use paid software to manage your business?",
        opts: [
          { a: "Yes, multiple tools", s: 4 },
          { a: "Yes, one main tool", s: 3 },
          { a: "Free tools only", s: 1 },
          { a: "No", s: 0 },
        ],
      },
    ],
  },
  {
    name: "mindset",
    label: "Mindset",
    icon: Lightbulb,
    questions: [
      {
        id: 25,
        pillar: "mindset",
        q: "Do you view marketing as an investment or a cost?",
        opts: [
          { a: "Investment", s: 4 },
          { a: "Cost", s: 0 },
        ],
      },
      {
        id: 26,
        pillar: "mindset",
        q: "How often do you review your business numbers?",
        opts: [
          { a: "Daily/Weekly", s: 4 },
          { a: "Monthly", s: 3 },
          { a: "Quarterly", s: 1 },
          { a: "Yearly or never", s: 0 },
        ],
      },
      {
        id: 27,
        pillar: "mindset",
        q: "Are you open to changing your business model to scale?",
        opts: [
          { a: "Yes, absolutely", s: 4 },
          { a: "Maybe", s: 2 },
          { a: "No", s: 0 },
        ],
      },
      {
        id: 28,
        pillar: "mindset",
        q: "Do you network with other business owners?",
        opts: [
          { a: "Yes, regularly", s: 4 },
          { a: "Occasionally", s: 2 },
          { a: "No", s: 0 },
        ],
      },
      {
        id: 29,
        pillar: "mindset",
        q: "How do you handle failure or setbacks?",
        opts: [
          { a: "Learn and adapt", s: 4 },
          { a: "Get frustrated but keep going", s: 2 },
          { a: "It takes me a while to recover", s: 1 },
        ],
      },
      {
        id: 30,
        pillar: "mindset",
        q: "What is your primary motivation for growth?",
        opts: [
          { a: "Building a legacy/freedom", s: 4 },
          { a: "Financial gain", s: 3 },
          { a: "Just keeping busy", s: 1 },
        ],
      },
    ],
  },
];

const QuizPage = () => {
  const [step, setStep] = useState("intro");
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [pillarScores, setPillarScores] = useState<Record<string, number>>({});
  const [totalScore, setTotalScore] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    name: "",
    businessName: "",
    email: "",
    phone: "",
    vans: "",
    consent: false,
  });

  const allQuestions = PILLARS.flatMap((p) => p.questions);
  const totalQuestions = allQuestions.length;

  const handleStart = () => {
    setStep("quiz");
    setCurrentIdx(0);
    setAnswers([]);
  };

  const handleAnswer = (s: number) => {
    const newAnswers = [...answers, s];
    setAnswers(newAnswers);
    if (currentIdx < totalQuestions - 1) {
      setCurrentIdx(currentIdx + 1);
    } else {
      calculateResults(newAnswers);
    }
  };

  const calculateResults = (finalAnswers: number[]) => {
    setStep("calculating");
    setTimeout(() => {
      const scores: Record<string, number> = {};
      let total = 0;
      PILLARS.forEach((pillar) => {
        const pillarQuestions = pillar.questions;
        const pillarStartIndex = allQuestions.findIndex(
          (q) => q.id === pillarQuestions[0].id,
        );
        const pillarAnswers = finalAnswers.slice(
          pillarStartIndex,
          pillarStartIndex + pillarQuestions.length,
        );
        const pillarTotal = pillarAnswers.reduce((a, b) => a + b, 0);
        scores[pillar.name] = pillarTotal;
        total += pillarTotal;
      });
      setPillarScores(scores);
      setTotalScore(total);
      setStep("results");
    }, 2000);
  };

  const handleBack = () => {
    if (currentIdx > 0) {
      setCurrentIdx(currentIdx - 1);
      setAnswers(answers.slice(0, -1));
    } else {
      setStep("intro");
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.consent) return;
    setIsSubmitting(true);
    try {
      await fetch(
        "https://services.leadconnectorhq.com/hooks/nsp8u9BgTJB40BULOWfw/webhook-trigger/bbe386c0-ad3a-4ae2-b91b-3c50364f008a",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...formData,
            totalScore,
            pillarScores,
            source: "scorecard_quiz",
          }),
        },
      );
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        colors: ["#004d8c", "#fe5240", "#ffffff"],
      });
      setShowDone(true);
    } catch (error) {
      console.error("Submission failed", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadPDF = async () => {
    if (!reportRef.current) return;
    const canvas = await html2canvas(reportRef.current);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save("Clean-Marketing-Growth-Scorecard.pdf");
  };

  const currentQuestion = allQuestions[currentIdx];
  const currentPillar = PILLARS.find((p) =>
    p.questions.some((q) => q.id === currentQuestion.id),
  );
  const progress = ((currentIdx + 1) / totalQuestions) * 100;

  const getVerdict = (score: number) => {
    if (score >= 100)
      return {
        band: "Scale-Ready",
        summary:
          "Your business has a rock-solid foundation and is perfectly positioned for aggressive growth. You have the systems, the mindset, and the investment capacity to dominate your market.",
        ctaHead: "You're ready to scale!",
        ctaSub:
          "Book a call and we'll show you how we can help you hit that next milestone even faster.",
      };
    if (score >= 70)
      return {
        band: "Growth-Ready",
        summary:
          "You're doing a lot of things right. You've professionalised several key areas of your business and you'll be ready to scale significantly soon. Focus on tightening your systems to close the remaining gaps.",
        ctaHead: "Ready to take the next step?",
        ctaSub:
          "Book a strategy call and we'll walk through your scorecard together to find the low-hanging fruit.",
      };
    if (score >= 40)
      return {
        band: "Foundations Forming",
        summary:
          "You've started building something real, but there are some critical gaps in your foundations that will hold you back from sustainable growth. Professionalising your systems and marketing approach is your next big win.",
        ctaHead: "Want to professionalise?",
        ctaSub:
          "Book a call and we'll show you how to build the foundations you need to scale properly.",
      };
    return {
      band: "Early Stage",
      summary:
        "You're in the early stages of professionalising your business. You have a massive opportunity ahead of you to build things correctly from day one. Focus on getting your basic systems and marketing foundations in place.",
      ctaHead: "Starting your journey?",
      ctaSub:
        "Book a call and we'll help you map out the fastest path to professionalising your window cleaning business.",
    };
  };

  const verdict = getVerdict(totalScore);

  return (
    <div className="min-h-screen bg-[#f4f7fb]">
      <SEOHead
        title="Window Cleaning Growth Scorecard | Clean Marketing"
        description="Answer 30 quick questions and get an instant Growth Scorecard for your window cleaning business with a free personalised PDF report."
        canonical="/quiz"
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "How long does the scorecard take?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "About 3 minutes. Just tap to answer 30 quick questions.",
              },
            },
            {
              "@type": "Question",
              name: "Do I get a report?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes, you get a free personalised PDF Growth Scorecard report.",
              },
            },
          ],
        }}
      />
      <Header />
      <main className="container mx-auto px-4 pt-32 pb-20 max-w-2xl">
        <div className="text-center mb-6">
          <img
            src="https://vibe.filesafe.space/1775166810156919871/attachments/07b67583-88fd-417b-bb44-aeaaee1d2dda.png"
            alt="Clean Marketing"
            className="h-12 mx-auto mb-2"
          />
          <div className="text-xs font-bold tracking-wider uppercase text-slate-500">
            <span className="text-secondary">Marketing</span>{" "}
            <span className="text-primary italic">Made Easy</span>{" "}
            <span className="text-secondary">For</span> Window Cleaners
          </div>
        </div>

        {step === "intro" && (
          <Card className="border-none shadow-xl rounded-2xl overflow-hidden">
            <CardContent className="p-10 text-center">
              <h1 className="text-3xl font-black text-secondary leading-tight mb-4">
                Is your business{" "}
                <span className="text-primary">ready to scale?</span>
              </h1>
              <p className="text-slate-600 mb-8 text-lg">
                Answer 30 quick questions about how you market your window
                cleaning business. Get an instant Growth Scorecard and a free
                personalised PDF report.
              </p>
              <div className="flex flex-wrap gap-2 justify-center mb-8">
                <span className="bg-slate-100 text-secondary text-xs font-bold px-3 py-1.5 rounded-full">
                  ⏱ ~3 minutes
                </span>
                <span className="bg-slate-100 text-secondary text-xs font-bold px-3 py-1.5 rounded-full">
                  👆 Just tap to answer
                </span>
                <span className="bg-slate-100 text-secondary text-xs font-bold px-3 py-1.5 rounded-full">
                  📄 Free PDF report
                </span>
              </div>
              <Button
                onClick={handleStart}
                size="lg"
                className="w-full h-14 text-lg bg-primary hover:bg-[#e04825]"
              >
                Start the Scorecard <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <p className="mt-4 text-xs text-slate-400">
                No typing. 5 growth areas scored out of 6.
              </p>
            </CardContent>
          </Card>
        )}

        {step === "quiz" && (
          <Card className="border-none shadow-xl rounded-2xl overflow-hidden">
            <CardContent className="p-10">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-secondary text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase">
                  {currentPillar?.label}
                </span>
                <span className="text-sm font-bold text-slate-400">
                  {currentIdx + 1} / {totalQuestions}
                </span>
              </div>
              <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden mb-8">
                <div
                  className="h-full bg-gradient-to-r from-primary to-[#ff7a6a] transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <h2 className="text-2xl font-bold text-secondary mb-6 leading-snug">
                {currentQuestion.q}
              </h2>
              <div className="space-y-3">
                {currentQuestion.opts.map((opt, i) => (
                  <Button
                    key={i}
                    variant="outline"
                    className="w-full h-auto py-4 text-left justify-start border-2 hover:border-primary hover:bg-white transition-all"
                    onClick={() => handleAnswer(opt.s)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full border-2 border-slate-200" />
                      {opt.a}
                    </div>
                  </Button>
                ))}
              </div>
              <Button
                variant="ghost"
                onClick={handleBack}
                className="mt-8 text-sm font-bold text-slate-400 hover:text-secondary"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
            </CardContent>
          </Card>
        )}

        {step === "calculating" && (
          <Card className="border-none shadow-xl rounded-2xl overflow-hidden">
            <CardContent className="p-16 text-center">
              <div className="w-14 h-14 border-4 border-slate-100 border-t-primary rounded-full animate-spin mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-secondary mb-2">
                Scoring your business...
              </h2>
              <p className="text-slate-500">Building your Growth Scorecard</p>
            </CardContent>
          </Card>
        )}

        {step === "results" && (
          <div className="space-y-6">
            <Card className="border-none shadow-xl rounded-2xl overflow-hidden">
              <CardContent className="p-10">
                <div className="text-center mb-8">
                  <div className="text-xs font-bold uppercase text-slate-400 mb-1">
                    Your Result
                  </div>
                  <h2 className="text-3xl font-black text-primary">
                    {verdict.band}
                  </h2>
                </div>

                <div className="relative w-40 h-40 mx-auto mb-8">
                  <svg
                    className="w-full h-full -rotate-90"
                    viewBox="0 0 100 100"
                  >
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="#e7ebf0"
                      strokeWidth="10"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="#fe5240"
                      strokeWidth="10"
                      strokeDasharray="282.7"
                      strokeDashoffset={282.7 - 282.7 * (totalScore / 120)}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-4xl font-black text-secondary leading-none">
                      {totalScore}
                    </span>
                    <span className="text-[10px] font-bold text-slate-400 uppercase">
                      out of 120
                    </span>
                  </div>
                </div>

                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 text-slate-700 text-sm leading-relaxed mb-8">
                  {verdict.summary}
                </div>

                <div className="space-y-6">
                  {PILLARS.map((pillar) => {
                    const score = (pillarScores as any)[pillar.name] || 0;
                    const maxPillarScore = pillar.questions.length * 4;
                    const percentage = (score / maxPillarScore) * 100;
                    return (
                      <div key={pillar.name} className="space-y-2">
                        <div className="flex justify-between items-end">
                          <span className="font-bold text-secondary text-sm">
                            {pillar.label}
                          </span>
                          <span className="font-black text-secondary text-sm">
                            {score}/{maxPillarScore}
                          </span>
                        </div>
                        <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all duration-1000"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="h-px bg-slate-100 my-10" />

                {!showDone ? (
                  <form onSubmit={handleFormSubmit} className="space-y-4">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold text-secondary">
                        Get your full PDF report
                      </h3>
                      <p className="text-sm text-slate-500 mt-2">
                        A personalised breakdown of every score — what you're
                        doing well, where the gaps are, and how to close them.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-bold text-slate-400 uppercase">
                          Your Name
                        </Label>
                        <Input
                          placeholder="e.g. James Savory"
                          required
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-bold text-slate-400 uppercase">
                          Business Name
                        </Label>
                        <Input
                          placeholder="e.g. Savory Window Cleaning"
                          required
                          value={formData.businessName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              businessName: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-bold text-slate-400 uppercase">
                          Email Address
                        </Label>
                        <Input
                          placeholder="you@business.co.uk"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-bold text-slate-400 uppercase">
                          Mobile Number
                        </Label>
                        <Input
                          placeholder="07…"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) =>
                            setFormData({ ...formData, phone: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-bold text-slate-400 uppercase">
                        How many vans do you run?
                      </Label>
                      <Select
                        onValueChange={(v) =>
                          setFormData({ ...formData, vans: v })
                        }
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            Just me / no vans yet
                          </SelectItem>
                          <SelectItem value="1">1 van</SelectItem>
                          <SelectItem value="2-3">2-3 vans</SelectItem>
                          <SelectItem value="4plus">4+ vans</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
                      <Checkbox
                        id="consent"
                        className="mt-0.5"
                        checked={formData.consent}
                        onCheckedChange={(c) =>
                          setFormData((prev) => ({
                            ...prev,
                            consent: c === true,
                          }))
                        }
                        required
                      />
                      <Label
                        htmlFor="consent"
                        className="text-xs text-slate-500 cursor-pointer leading-relaxed"
                      >
                        I'm happy for Clean Marketing to send me my report and
                        occasional marketing. You can opt out any time.
                      </Label>
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full h-14 text-lg bg-primary hover:bg-[#e04825] shadow-lg shadow-primary/20"
                    >
                      {isSubmitting && (
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      )}
                      Generate my PDF report{" "}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </form>
                ) : (
                  <div className="text-center space-y-6">
                    <div className="h-16 w-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircle2 className="h-10 w-10" />
                    </div>
                    <h3 className="text-2xl font-bold text-secondary">
                      Your report is ready 🎉
                    </h3>
                    <p className="text-slate-500">
                      Tap below to download your personalised Growth Scorecard.
                    </p>
                    <Button
                      onClick={downloadPDF}
                      size="lg"
                      className="w-full h-14 text-lg bg-secondary hover:bg-secondary/90"
                    >
                      <Download className="mr-2 h-5 w-5" /> Download my PDF
                      report
                    </Button>
                    <div className="bg-gradient-to-br from-secondary to-[#0a5ea6] p-8 rounded-2xl text-white text-center">
                      <h4 className="text-xl font-bold mb-2">
                        {verdict.ctaHead}
                      </h4>
                      <p className="text-blue-100 text-sm mb-6">
                        {verdict.ctaSub}
                      </p>
                      <Button
                        asChild
                        className="bg-primary text-white hover:bg-[#e04825] px-10 h-14 text-lg font-bold"
                      >
                        <Link to="/book-demo">
                          Book a call <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                    <p className="text-[10px] text-slate-400 font-medium">
                      £2.7M+ recurring revenue booked · 60,000+ leads processed
                      · 50+ window cleaning businesses
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        <div className="fixed left-[-9999px] top-0 overflow-hidden">
          <div
            ref={reportRef}
            className="w-[800px] p-12 bg-white text-slate-900"
          >
            <div className="flex justify-between items-start mb-8">
              <div>
                <img
                  src="https://vibe.filesafe.space/1775166810156919871/attachments/07b67583-88fd-417b-bb44-aeaaee1d2dda.png"
                  alt="Clean Marketing"
                  className="h-12 mb-2"
                />
                <div className="text-[10px] font-bold tracking-widest uppercase text-slate-400">
                  Growth Scorecard Report
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-black text-secondary">
                  {totalScore}/120
                </div>
                <div className="text-xs font-bold text-primary uppercase tracking-wider">
                  {verdict.band}
                </div>
              </div>
            </div>
            <div className="h-1 bg-gradient-to-r from-secondary to-primary w-full mb-10 rounded-full" />
            <div className="grid grid-cols-2 gap-12 mb-12">
              <div>
                <h2 className="text-2xl font-bold text-secondary mb-4">
                  Overall Performance
                </h2>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {verdict.summary}
                </p>
              </div>
              <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100 text-center flex flex-col justify-center">
                <div className="text-6xl font-black text-secondary">
                  {Math.round((totalScore / 120) * 100)}%
                </div>
                <div className="text-primary font-bold uppercase text-[10px] tracking-widest mt-2">
                  Growth Potential
                </div>
              </div>
            </div>

            <div className="space-y-8 mb-12">
              <h3 className="text-lg font-bold text-secondary uppercase tracking-widest border-b pb-2">
                Pillar Breakdown
              </h3>
              {PILLARS.map((pillar) => {
                const score = (pillarScores as any)[pillar.name] || 0;
                const maxPillarScore = pillar.questions.length * 4;
                const percentage = (score / maxPillarScore) * 100;
                return (
                  <div key={pillar.name} className="space-y-3">
                    <div className="flex justify-between items-baseline">
                      <span className="font-bold text-secondary text-base">
                        {pillar.label}
                      </span>
                      <span className="font-black text-secondary text-base">
                        {score}/{maxPillarScore}
                      </span>
                    </div>
                    <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bg-blue-50 border border-blue-100 p-8 rounded-3xl mb-12">
              <h3 className="text-xl font-bold text-secondary mb-4">
                Next Steps for {formData.name || "Your Business"}
              </h3>
              <p className="text-slate-700 text-sm leading-relaxed mb-6">
                Based on your score of {totalScore}, you are currently in the{" "}
                <strong>{verdict.band}</strong> phase. To move to the next
                level, focus on automating your lead follow-up and
                professionalising your brand presence.
              </p>
              <div className="flex items-center gap-4 text-secondary font-bold">
                <div className="bg-white p-3 rounded-xl shadow-sm">
                  <ArrowRight className="h-5 w-5 text-primary" />
                </div>
                <span>Book your strategy call at cleanmarketing.co.uk</span>
              </div>
            </div>

            <div className="text-center pt-8 border-t">
              <p className="text-[10px] text-slate-400 font-medium">
                © {new Date().getFullYear()} Clean Marketing · Built for Window
                Cleaners by Window Cleaners
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default QuizPage;
